import { useMemo } from "react";
import { AlertTriangle, DollarSign, Eye, Monitor, Percent, TrendingUp, Users } from "lucide-react";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, Line } from "recharts";
import { SummaryCard, PlatformBadge } from "./SummaryCard";
import type { UsageSummary } from "../../types";

interface UsageTimeseriesBundle {
  labels: string[];
  series: {
    sessions: number[];
    dailyActiveDevices: number[];
    crashes: number[];
    payingUsers: number[];
    appStoreViews: number[];
  };
}

interface UsageTabContentProps {
  loadingSummary: boolean;
  usageSummary?: UsageSummary;
  loadingUsageTimeseries: boolean;
  usageTimeseries?: UsageTimeseriesBundle;
}

export function UsageTabContent({
  loadingSummary,
  usageSummary,
  loadingUsageTimeseries,
  usageTimeseries,
}: UsageTabContentProps) {
  const crashRate = useMemo(() => {
    if (!usageSummary?.sessions || !usageSummary?.crashes) return 0;
    return (usageSummary.crashes / usageSummary.sessions) * 100;
  }, [usageSummary]);

  const engagementRate = useMemo(() => {
    if (!usageSummary?.monthly_active_devices || !usageSummary?.daily_active_devices) return 0;
    return (usageSummary.daily_active_devices / usageSummary.monthly_active_devices) * 100;
  }, [usageSummary]);

  const usageChartData = useMemo(() => {
    if (!usageTimeseries?.labels || !usageTimeseries.series) return [];

    return usageTimeseries.labels.map((label, index) => ({
      date: new Date(label).toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
      }),
      sessions: usageTimeseries.series.sessions[index] || 0,
      dailyActiveDevices: usageTimeseries.series.dailyActiveDevices[index] || 0,
    }));
  }, [usageTimeseries]);

  const crashesChartData = useMemo(() => {
    if (!usageTimeseries?.labels || !usageTimeseries.series) return [];

    return usageTimeseries.labels.map((label, index) => ({
      date: new Date(label).toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
      }),
      crashes: usageTimeseries.series.crashes[index] || 0,
    }));
  }, [usageTimeseries]);

  const payingUsersChartData = useMemo(() => {
    if (!usageTimeseries?.labels || !usageTimeseries.series) return [];

    return usageTimeseries.labels.map((label, index) => ({
      date: new Date(label).toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
      }),
      payingUsers: usageTimeseries.series.payingUsers[index] || 0,
    }));
  }, [usageTimeseries]);

  const appStoreViewsChartData = useMemo(() => {
    if (!usageTimeseries?.labels || !usageTimeseries.series) return [];

    return usageTimeseries.labels.map((label, index) => ({
      date: new Date(label).toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
      }),
      views: usageTimeseries.series.appStoreViews[index] || 0,
    }));
  }, [usageTimeseries]);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SummaryCard
          title="Sessions"
          value={usageSummary?.sessions ?? 0}
          loading={loadingSummary}
          format="number"
          icon={TrendingUp}
          color="bg-[#22C55E33]"
          tooltip="Total de sessões de uso do aplicativo. Uma sessão é um período de uso contínuo. Disponível em iOS e Android."
        />
        <SummaryCard
          title="Daily Active Devices"
          value={usageSummary?.daily_active_devices ?? 0}
          loading={loadingSummary}
          format="number"
          icon={DollarSign}
          color="bg-[#3B82F633]"
          tooltip="Número de dispositivos únicos que usaram o aplicativo diariamente. Disponível em iOS e Android."
        />
        <SummaryCard
          title="Monthly Active Devices"
          value={usageSummary?.monthly_active_devices ?? 0}
          loading={loadingSummary}
          format="number"
          icon={Percent}
          color="bg-[#8B5CF633]"
          platform="apple"
          tooltip="Número de dispositivos únicos que usaram o aplicativo mensalmente. Disponível apenas no iOS (Apple Analytics)."
        />
        <SummaryCard
          title="Paying Users"
          value={usageSummary?.paying_users ?? 0}
          loading={loadingSummary}
          format="number"
          icon={DollarSign}
          color="bg-[#F59E0B33]"
          platform="apple"
          tooltip="Número de usuários que fizeram compras no aplicativo. Disponível apenas no iOS (Apple Analytics)."
        />
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Engajamento e Saúde</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SummaryCard
            title="Engagement Rate"
            value={engagementRate}
            loading={loadingSummary}
            format="percentage"
            icon={TrendingUp}
            color="bg-[#8B5CF633]"
            platform="apple"
            tooltip="Porcentagem de usuários mensais que estão ativos diariamente. Quanto maior, melhor o engajamento. (DAU/MAU * 100)"
          />
          <SummaryCard
            title="Sessions per Device"
            value={usageSummary?.sessions_per_device ?? 0}
            loading={loadingSummary}
            format="number"
            icon={Monitor}
            color="bg-[#3B82F633]"
            tooltip="Número médio de sessões por dispositivo. Disponível em iOS e Android."
          />
          <SummaryCard
            title="Crash Rate"
            value={crashRate}
            loading={loadingSummary}
            format="percentage"
            icon={AlertTriangle}
            color="bg-[#EF444433]"
            tooltip="Taxa de crashes por sessão. Quanto menor, melhor a estabilidade do app. (Crashes/Sessions * 100)"
          />
          <SummaryCard
            title="Avg Opt-in Rate"
            value={usageSummary?.avg_optin_rate ?? 0}
            loading={loadingSummary}
            format="percentage"
            icon={Percent}
            color="bg-[#22C55E33]"
            platform="apple"
            tooltip="Taxa média de opt-in (aceitação de notificações/permissões). Disponível apenas no iOS (Apple Analytics)."
          />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Descoberta na Loja</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SummaryCard
            title="App Store Views"
            value={usageSummary?.app_store_views ?? 0}
            loading={loadingSummary}
            format="number"
            icon={TrendingUp}
            color="bg-[#22C55E33]"
            tooltip="Total de visualizações da página do aplicativo nas lojas. Disponível em iOS (App Store) e Android (Google Play)."
          />
          <SummaryCard
            title="Unique App Store Views"
            value={usageSummary?.unique_app_store_views ?? 0}
            loading={loadingSummary}
            format="number"
            icon={DollarSign}
            color="bg-[#3B82F633]"
            platform="apple"
            tooltip="Visualizações únicas da página do aplicativo na App Store. Disponível apenas no iOS."
          />
          <SummaryCard
            title="Impressions"
            value={usageSummary?.impressions ?? 0}
            loading={loadingSummary}
            format="number"
            icon={Eye}
            color="bg-[#8B5CF633]"
            platform="apple"
            tooltip="Total de impressões (exibições) do aplicativo em resultados de busca. Disponível apenas no iOS (App Store)."
          />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Evolução do Engajamento</h2>
        <div className="bg-[#232323] border-none rounded-lg">
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">Sessões e Dispositivos Ativos Diários</h3>
              <p className="text-sm text-white/60">Evolução do engajamento dos usuários ao longo do tempo</p>
            </div>
            <div style={{ width: "100%", height: 400 }}>
              {loadingUsageTimeseries ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Carregando dados...</p>
                </div>
              ) : usageChartData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Sem dados disponíveis para o período selecionado</p>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={usageChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "#ffffff80" }} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "#232323",
                        border: "1px solid #ffffff20",
                        borderRadius: "8px",
                        color: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                      formatter={(value: number, name: string) => [
                        new Intl.NumberFormat("pt-BR").format(value),
                        name === "sessions" ? "Sessões" : "Dispositivos Ativos",
                      ]}
                    />
                    <Legend
                      wrapperStyle={{ color: "#ffffff80", fontSize: "12px" }}
                      formatter={(value: string) => (value === "sessions" ? "Sessões" : "Dispositivos Ativos")}
                    />
                    <Line
                      type="monotone"
                      dataKey="sessions"
                      stroke="#22C55E"
                      strokeWidth={3}
                      dot={{ fill: "#22C55E", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#22C55E", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="dailyActiveDevices"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#3B82F6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Estabilidade do Aplicativo</h2>
        <div className="bg-[#232323] border-none rounded-lg">
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Crashes por Período
              </h3>
              <p className="text-sm text-white/60">Monitoramento de crashes ao longo do tempo</p>
            </div>
            <div style={{ width: "100%", height: 400 }}>
              {loadingUsageTimeseries ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Carregando dados...</p>
                </div>
              ) : crashesChartData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Sem dados disponíveis para o período selecionado</p>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={crashesChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "#ffffff80" }} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "#232323",
                        border: "1px solid #ffffff20",
                        borderRadius: "8px",
                        color: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                      formatter={(value: number) => [new Intl.NumberFormat("pt-BR").format(value), "Crashes"]}
                    />
                    <Legend wrapperStyle={{ color: "#ffffff80", fontSize: "12px" }} formatter={() => "Crashes"} />
                    <Line
                      type="monotone"
                      dataKey="crashes"
                      stroke="#EF4444"
                      strokeWidth={3}
                      dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#EF4444", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Usuários Pagantes</h2>
          <PlatformBadge platform="apple" />
        </div>
        <div className="bg-[#232323] border-none rounded-lg">
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" />
                Evolução de Paying Users
              </h3>
              <p className="text-sm text-white/60">
                Usuários que fizeram pelo menos uma compra no período.{" "}
                <span className="text-blue-400 ml-1">Disponível apenas no iOS (Apple Analytics)</span>
              </p>
            </div>
            <div style={{ width: "100%", height: 400 }}>
              {loadingUsageTimeseries ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Carregando dados...</p>
                </div>
              ) : payingUsersChartData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Sem dados disponíveis para o período selecionado</p>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={payingUsersChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "#ffffff80" }} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "#232323",
                        border: "1px solid #ffffff20",
                        borderRadius: "8px",
                        color: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                      formatter={(value: number) => [new Intl.NumberFormat("pt-BR").format(value), "Usuários Pagantes"]}
                    />
                    <Legend wrapperStyle={{ color: "#ffffff80", fontSize: "12px" }} formatter={() => "Usuários Pagantes"} />
                    <Line
                      type="monotone"
                      dataKey="payingUsers"
                      stroke="#10B981"
                      strokeWidth={3}
                      dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#10B981", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Descoberta do Aplicativo</h2>
        <div className="bg-[#232323] border-none rounded-lg">
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-500" />
                Visualizações na Loja por Período
              </h3>
              <p className="text-sm text-white/60">
                Número de visualizações da página do aplicativo nas lojas (App Store e Google Play) ao longo do tempo
              </p>
            </div>
            <div style={{ width: "100%", height: 400 }}>
              {loadingUsageTimeseries ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Carregando dados...</p>
                </div>
              ) : appStoreViewsChartData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Sem dados disponíveis para o período selecionado</p>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={appStoreViewsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "#ffffff80" }} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "#232323",
                        border: "1px solid #ffffff20",
                        borderRadius: "8px",
                        color: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                      formatter={(value: number) => [new Intl.NumberFormat("pt-BR").format(value), "Visualizações"]}
                    />
                    <Legend wrapperStyle={{ color: "#ffffff80", fontSize: "12px" }} formatter={() => "Visualizações na Loja"} />
                    <Line
                      type="monotone"
                      dataKey="views"
                      stroke="#8B5CF6"
                      strokeWidth={3}
                      dot={{ fill: "#8B5CF6", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#8B5CF6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

